/**
 * Project-scoped persistence for completed TRT (Bourdet) analyses.
 *
 * One analysis per test (multiple per project: different test wells,
 * different test conditions). Each scenario in the same project picks
 * WHICH analysis (if any) drives its formation k/α calibration via
 * its own `selectedTrtAnalysisId` field — that lives on the scenario,
 * managed by projectSlice.
 *
 * This slice owns only the analyses themselves. Scenario-level
 * pointers and the actual override application live in
 * `features/formations/` and `features/projects/`.
 *
 * Lifecycle:
 *   - Wizard completes refine → dispatches `addAnalysis(record)`.
 *   - Engineer browses the TRT sidebar page → reads via
 *     `selectAnalysesForProject(projectId)`.
 *   - Engineer opens Formation Properties for a scenario, picks a
 *     TRT to calibrate against → projectSlice writes
 *     `scenario.selectedTrtAnalysisId`.
 *   - Engineer deletes an analysis → `removeAnalysis(id)` is called.
 *     B-50 Step 2 follow-up: scenarios pointing at the deleted ID
 *     fall back to lithology estimates (the projectSlice handler
 *     for this lives there, not here).
 */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { TrtAnalysisRecord } from "./types";

export interface TrtResultsState {
  /** Record keyed by analysis ID. Multiple per project; the project ID
   *  is on each record. We could nest by projectId but a flat map keeps
   *  the data-access cost the same and avoids juggling two keys. */
  analyses: Record<string, TrtAnalysisRecord>;
}

const initialState: TrtResultsState = {
  analyses: {},
};

const trtResultsSlice = createSlice({
  name: "trtResults",
  initialState,
  reducers: {
    /** Wizard calls this when refine completes and the engineer saves
     *  the analysis to the project. */
    addAnalysis(state, action: PayloadAction<TrtAnalysisRecord>) {
      state.analyses[action.payload.id] = action.payload;
    },
    /** Rename / re-label without re-running. */
    renameAnalysis(
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) {
      const a = state.analyses[action.payload.id];
      if (a) a.label = action.payload.label;
    },
    /** Delete one analysis. Scenarios that reference the ID become
     *  "calibration-less" (fall back to lithology) — the cleanup of
     *  scenario.selectedTrtAnalysisId happens in projectSlice. */
    removeAnalysis(state, action: PayloadAction<string>) {
      delete state.analyses[action.payload];
    },
    /** Bulk delete on project removal. Called by projectSlice when a
     *  project is deleted (orphaned analyses would otherwise leak in
     *  the persisted store). */
    removeAnalysesForProject(state, action: PayloadAction<string>) {
      for (const id of Object.keys(state.analyses)) {
        if (state.analyses[id]!.projectId === action.payload) {
          delete state.analyses[id];
        }
      }
    },
  },
});

export const {
  addAnalysis,
  renameAnalysis,
  removeAnalysis,
  removeAnalysesForProject,
} = trtResultsSlice.actions;

export const trtResultsReducer = trtResultsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectAllAnalyses = (state: {
  trtResults: TrtResultsState;
}): TrtAnalysisRecord[] => Object.values(state.trtResults.analyses);

/** All TRT analyses for a given project, newest-first. */
export const selectAnalysesForProject =
  (projectId: string | null) =>
  (state: { trtResults: TrtResultsState }): TrtAnalysisRecord[] => {
    if (!projectId) return [];
    return Object.values(state.trtResults.analyses)
      .filter((a) => a.projectId === projectId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  };

/** One analysis by ID — used by scenarios that have
 *  `selectedTrtAnalysisId` set. Returns undefined when the analysis
 *  was deleted (scenario should fall back to lithology). */
export const selectAnalysisById =
  (id: string | null | undefined) =>
  (state: { trtResults: TrtResultsState }): TrtAnalysisRecord | undefined => {
    if (!id) return undefined;
    return state.trtResults.analyses[id];
  };
